import { getDetachedDay } from "@laborchart-modules/common/dist/datetime";
import { timeStringToFloat } from "@/lib/utils/date-2";
import { TaskType } from "./prop-types";
import { RequestStore } from "@/stores/request-store.core";
import { Assignment2Store } from "@/stores/assignment-2-store.core";
import type { WorkDays } from "@laborchart-modules/common/dist/postgres/schemas/common/custom-json";

export interface UnassignTaskData {
   id: string;
   startDate: Date;
   endDate: Date;
   startTime: string | null;
   endTime: string | null;
   jobTitleId: string | null;
   projectId: string;
   workDays: WorkDays;
   categoryId: string | null;
   instructionText: string | null;
   percentAllocated: number | null;
   statusId: string | null;
   subcategoryId: string | null;
   tagIds: string[];
   workScopeText: string | null;
}

export interface UnassignResult {
   newRequestId: string;
   taskType: TaskType;
   taskName: string;
}

/**
 * Handles the unassignment of a task by converting an assignment to a request.
 * This involves creating a new request record and deleting the existing assignment.
 *
 * @param taskData - The task data to unassign
 * @returns Promise resolving to the new request ID and task metadata
 * @throws Error if the unassignment process fails
 */
export async function handleUnassignTask(taskData: UnassignTaskData): Promise<UnassignResult> {
   // Convert dates to detached day format
   const payloadStartDay = getDetachedDay(new Date(taskData.startDate));
   const payloadEndDay = getDetachedDay(new Date(taskData.endDate));

   // Convert time strings to float format
   let payloadStartTime: number | null = null;
   if (taskData.startTime != null) {
      payloadStartTime = timeStringToFloat(taskData.startTime);
   }

   let payloadEndTime: number | null = null;
   if (taskData.endTime != null) {
      payloadEndTime = timeStringToFloat(taskData.endTime);
   }

   // Create the request payload
   const newRequestResponse = await RequestStore.createRequest({
      start_day: payloadStartDay,
      end_day: payloadEndDay,
      job_title_id: taskData.jobTitleId ?? null,
      project_id: taskData.projectId,
      work_days: taskData.workDays,
      category_id: taskData.categoryId,
      end_time: payloadEndTime,
      instruction_text: taskData.instructionText,
      percent_allocated: taskData.percentAllocated,
      start_time: payloadStartTime,
      status_id: taskData.statusId,
      subcategory_id: taskData.subcategoryId,
      tag_ids: taskData.tagIds,
      work_scope_text: taskData.workScopeText,
   }).payload;

   // Delete the assignment
   await Assignment2Store.deleteAssignment(String(taskData.id)).payload;

   // Return the result data
   return {
      newRequestId: newRequestResponse.data[0].id,
      taskType: TaskType.REQUEST,
      taskName: "Request",
   };
}
